import { Box, createStyles, Tooltip } from "@mantine/core";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import swal from "sweetalert";
import moment from "moment";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import ClaimToolbar from "../../../components/ClaimBilling/ClaimToolbar";
import TableLoader from "../../../components/TableLoader";
import RecordNotFound from "../../../components/RecordNotFound";
import { getAllBillingModal, removeBilling, editBilling } from "../../../services";
import { getAllPatients } from "../../../services/patient";
import { decrypt, encrypt } from "../../../utils/index";
import {
  LuArrowLeft,
  LuChevronLeft,
  LuChevronRight,
  LuDownload,
  LuAlertTriangle,
  LuTrash2,
  LuX,
} from "react-icons/lu";

/* ─── design tokens ─────────────────────────────────────────────────────── */
const INK       = "#171717";
const INK2      = "#262626";
const MUTED     = "#667085";
const LINE      = "#E5E5E5";
const LINE_SOFT = "#F1F1F1";
const SURFACE_SOFT = "#FAFAFA";
const SURFACE_ALT  = "#F4F4F5";
const RED       = "#B91C1C";
const RED_SOFT  = "#FEF2F2";
const RED_BORDER = "#FCA5A5";

/* ─── styles ─────────────────────────────────────────────────────────────── */
const useStyles = createStyles(() => ({
  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    height: 34,
    padding: "0 12px",
    borderRadius: 7,
    border: `1px solid ${LINE}`,
    background: "transparent",
    color: INK2,
    fontSize: 12.5,
    fontWeight: 600,
    width: "fit-content",
    cursor: "pointer",
    margin: "16px 0 12px",
    "& svg": { width: 15, height: 15 },
    "&:hover": { background: SURFACE_ALT },
  },

  /* ── rejected grid ── */
  rejectedGrid: {
    display: "grid",
    gridTemplateColumns: "360px minmax(0, 1fr)",
    gap: 16,
    alignItems: "start",
    position: "relative",
    transition: "grid-template-columns .25s ease, gap .25s ease, padding-left .25s ease",
  },
  rejectedGridCollapsed: {
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: 0,
    paddingLeft: 44,
  },
  listHidden: {
    display: "none",
  },

  /* ── collapse arrow ── */
  gridCollapseArrow: {
    position: "absolute",
    top: 120,
    left: 360,
    transform: "translate(-50%, 0)",
    width: 26,
    height: 26,
    background: "#fff",
    border: `1px solid ${LINE}`,
    borderRadius: 999,
    cursor: "pointer",
    zIndex: 5,
    display: "grid",
    placeItems: "center",
    color: INK2,
    padding: 0,
    boxShadow: "0 2px 6px rgba(15,15,20,0.08)",
    transition:
      "left .25s ease, top .25s ease, width .25s ease, height .25s ease, border-radius .25s ease, background .12s ease",
    "& svg": { width: 14, height: 14 },
    "&:hover": { background: SURFACE_SOFT, borderColor: INK2 },
  },
  gridCollapseArrowCollapsed: {
    left: 0,
    top: 64,
    transform: "translate(0, 0)",
    width: 36,
    height: "auto",
    minHeight: 150,
    borderRadius: 10,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    padding: "14px 0",
    background: INK,
    color: "#fff",
    borderColor: INK,
    boxShadow: "0 4px 12px rgba(15,15,20,0.18)",
    "& svg": { width: 16, height: 16, transform: "rotate(180deg)" },
    "&:hover": { background: "#1A1A1A", borderColor: "#1A1A1A" },
  },
  gcIcon: {
    display: "grid",
    placeItems: "center",
    "& svg": { width: 14, height: 14 },
  },
  gcLabel: {
    writingMode: "vertical-rl",
    transform: "rotate(180deg)",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    whiteSpace: "nowrap",
    color: "#fff",
  },

  /* ── left list ── */
  rjList: {
    background: "#fff",
    border: `1px solid ${LINE}`,
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    position: "sticky",
    top: 16,
    height: "100vh",
    display: "flex",
    flexDirection: "column",
  },
  rjListHead: {
    padding: "12px 16px",
    borderBottom: `1px solid ${LINE}`,
    background: SURFACE_SOFT,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    "& h3": {
      margin: 0,
      fontSize: 14,
      fontWeight: 700,
      color: INK,
      letterSpacing: "0.01em",
    },
  },
  listScroll: { flex: 1, overflowY: "auto" },
  rjCard: {
    display: "block",
    padding: "14px 16px",
    background: "#fff",
    border: 0,
    borderBottom: `1px solid ${LINE_SOFT}`,
    cursor: "pointer",
    transition: "background .12s ease",
    width: "100%",
    textAlign: "left",
    "&:last-child": { borderBottom: 0 },
    "&:hover": { background: SURFACE_SOFT },
  },
  rjCardSelected: {
    background: SURFACE_SOFT,
    boxShadow: `inset 3px 0 0 ${RED}`,
  },
  rjCardRow1: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  rjCardId: {
    fontSize: 12,
    fontWeight: 700,
    color: INK,
    letterSpacing: "0.02em",
    fontFamily: "ui-monospace, Menlo, monospace",
  },
  rjCardAmt: { fontSize: 13, fontWeight: 700, color: INK },
  rjCardName: { margin: "6px 0 4px", fontSize: 13.5, fontWeight: 600, color: INK },
  rjCardPayer: { fontSize: 12, color: MUTED, lineHeight: 1.5 },
  cardProvider: { display: "block", color: MUTED, "& b": { color: INK } },
  rjCardReason: {
    marginTop: 10,
    fontSize: 11.5,
    color: RED,
    background: RED_SOFT,
    border: `1px solid ${RED_BORDER}`,
    borderRadius: 6,
    padding: "8px 10px",
    lineHeight: 1.45,
    "& b": { fontWeight: 700, color: "#7F1D1D" },
  },

  /* ── right — page id ── */
  pageId: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 14,
    "& h2": {
      margin: 0,
      fontSize: 20,
      fontWeight: 700,
      color: INK,
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
    },
  },
  countBadge: {
    display: "inline-grid",
    placeItems: "center",
    minWidth: 32,
    height: 32,
    padding: "0 8px",
    borderRadius: 999,
    fontSize: 13.5,
    fontWeight: 800,
    background: "#FEE2E2",
    color: RED,
    border: `1.5px solid ${RED_BORDER}`,
  },

  /* ── status trigger ── */
  statusTrigger: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px 6px 10px",
    border: `1px solid ${LINE}`,
    borderRadius: 999,
    background: "#fff",
    fontSize: 12.5,
    fontWeight: 600,
    color: RED,
    marginBottom: 14,
    cursor: "pointer",
    "& svg": { width: 13, height: 13 },
    "&:hover": { background: SURFACE_SOFT },
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 999,
    background: "currentColor",
    flexShrink: 0,
  },
  statusLabel: {
    color: MUTED,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontSize: 11,
  },

  /* ── cms form ── */
  cmsForm: {
    background: "#fff",
    border: `1px solid ${LINE}`,
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
  },
  cmsFormHead: {
    padding: "12px 18px",
    borderBottom: `1px solid ${LINE_SOFT}`,
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    gap: 14,
  },
  headPatient: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
  avatar: {
    width: 36,
    height: 36,
    borderRadius: 999,
    background: "#FFF4DA",
    color: "#B7791F",
    fontSize: 13,
    fontWeight: 700,
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    overflow: "hidden",
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  headInfo: { display: "flex", flexDirection: "column", minWidth: 0 },
  headName: { fontSize: 13, fontWeight: 700, color: INK },
  headMeta: { fontSize: 11.5, color: MUTED },
  headTitle: {
    margin: 0,
    fontSize: 14.5,
    fontWeight: 700,
    color: INK,
    justifySelf: "center",
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    whiteSpace: "nowrap",
  },
  claimIdMono: {
    fontFamily: "ui-monospace, Menlo, monospace",
    fontSize: 13,
    fontWeight: 600,
    color: INK2,
    padding: "2px 8px",
    background: SURFACE_SOFT,
    borderRadius: 5,
  },
  cmsNavArrow: {
    width: 26,
    height: 26,
    border: 0,
    background: "transparent",
    borderRadius: 6,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    color: INK2,
    "& svg": { width: 16, height: 16 },
    "&:hover": { background: SURFACE_SOFT, color: INK },
    "&:disabled": { opacity: 0.3, cursor: "not-allowed" },
  },
  headActions: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
  },
  downloadBtn: {
    width: 32,
    height: 32,
    borderRadius: 7,
    border: `1px solid ${LINE}`,
    background: "#fff",
    color: INK2,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    "& svg": { width: 14, height: 14 },
    "&:hover": { background: SURFACE_ALT },
  },

  /* ── cms paper ── */
  paper: { padding: "18px 20px", background: SURFACE_SOFT, zoom: 1.1 },
  titleBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    background: INK,
    color: "#FFBA45",
    borderRadius: "6px 6px 0 0",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },

  /* ── cms grid ── */
  cmsGrid: {
    border: `1px solid ${INK}`,
    borderTop: 0,
    background: "#fff",
  },
  cmsGridMt: {
    border: `1px solid ${INK}`,
    borderTop: 0,
    background: "#fff",
    marginTop: 8,
  },
  cmsRow: {
    display: "flex",
    borderBottom: `1px solid ${INK}`,
    "&:last-child": { borderBottom: 0 },
  },
  cmsCell: {
    padding: "6px 8px",
    borderRight: `1px solid ${INK}`,
    flex: 1,
    minWidth: 0,
    "&:last-child": { borderRight: 0 },
  },
  flex2: { flex: 2 },
  flex3: { flex: 3 },
  cmsNum: {
    fontSize: 9,
    fontWeight: 700,
    color: MUTED,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: 2,
  },
  cmsVal: {
    fontSize: 12,
    color: INK,
    fontWeight: 500,
    fontFamily: "'Courier New', monospace",
    lineHeight: 1.4,
    minHeight: 16,
    wordBreak: "break-word",
    border: "1px dashed transparent",
    borderRadius: 3,
    padding: "2px 4px",
    cursor: "text",
    outline: "none",
    transition: "border-color .1s, background .1s",
    "&:hover": { borderColor: "#FFBA45", background: "#FFF8E5" },
    "&:focus": {
      borderColor: "#FFBA45",
      background: "#fff",
      boxShadow: "0 0 0 3px rgba(255,186,69,0.18)",
    },
  },

  /* ── cms services table ── */
  cmsServices: {
    border: `1px solid ${INK}`,
    borderTop: 0,
    background: "#fff",
    marginTop: 8,
    overflowX: "auto",
    "& table": {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: 12,
    },
    "& thead th": {
      background: SURFACE_ALT,
      fontSize: 9.5,
      fontWeight: 700,
      color: MUTED,
      textTransform: "uppercase",
      letterSpacing: "0.04em",
      padding: "6px 8px",
      borderRight: `1px solid ${INK}`,
      borderBottom: `1px solid ${INK}`,
      textAlign: "left",
      fontFamily: "Inter, sans-serif",
      "&:last-child": { borderRight: 0 },
    },
    "& tbody td": {
      padding: 8,
      borderRight: `1px solid ${LINE_SOFT}`,
      borderBottom: `1px solid ${LINE_SOFT}`,
      fontFamily: "'Courier New', monospace",
      fontSize: 12,
      color: INK,
      "&:last-child": { borderRight: 0 },
    },
    "& tbody tr:last-child td": { borderBottom: 0 },
    "& tbody tr.errorRow td": {
      background: RED_SOFT,
      color: RED,
    },
  },

  /* ── foot ── */
  cmsFormFoot: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 10,
    padding: "14px 20px",
    borderTop: `1px solid ${LINE_SOFT}`,
    background: "#fff",
  },
  deleteClaimBtn: {
    width: 36,
    height: 36,
    border: `1px solid ${RED_BORDER}`,
    background: "#fff",
    color: RED,
    borderRadius: 8,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    "& svg": { width: 16, height: 16 },
    "&:hover": { background: RED_SOFT },
  },
  resubmitBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    height: 40,
    padding: "0 16px",
    borderRadius: 8,
    border: "1px solid transparent",
    background: "#FFBA45",
    color: INK,
    fontSize: 13.5,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "Inter, sans-serif",
    whiteSpace: "nowrap",
    transition: "background .12s ease",
    "&:hover:not(:disabled)": { background: "#EEAA33" },
    "&:disabled": { opacity: 0.45, cursor: "not-allowed" },
  },

  /* ── status popup ── */
  statusPopupOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 400,
    background: "rgba(15,23,42,0.45)",
    display: "grid",
    placeItems: "center",
    padding: 24,
    zIndex: 1001
  },
  statusPopup: {
    width: "100%",
    maxWidth: 520,
    maxHeight: "84vh",
    display: "flex",
    flexDirection: "column",
    background: "#fff",
    borderRadius: 14,
    boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
    overflow: "hidden",
  },
  statusPopupHead: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    padding: "18px 22px 12px",
    borderBottom: `1px solid ${LINE}`,
    "& h3": { margin: 0, fontSize: 17, fontWeight: 700, color: INK, letterSpacing: "-0.01em" },
  },
  statusPopupSub: { margin: "2px 0 0", fontSize: 12.5, color: MUTED },
  statusPopupClose: {
    width: 28,
    height: 28,
    border: 0,
    background: "transparent",
    borderRadius: 6,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    color: MUTED,
    flexShrink: 0,
    "& svg": { width: 14, height: 14 },
    "&:hover": { background: SURFACE_SOFT },
  },
  statusPopupBody: { padding: "18px 22px 22px", overflowY: "auto" },
  statusEvents: {
    position: "relative",
    paddingLeft: 26,
    display: "flex",
    flexDirection: "column",
    gap: 18,
    "&::before": {
      content: '""',
      position: "absolute",
      left: 9,
      top: 5,
      bottom: 5,
      width: 2,
      background: LINE,
      borderRadius: 2,
    },
  },
  statusEvent: {
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      left: -22,
      top: 4,
      width: 14,
      height: 14,
      borderRadius: 999,
      background: "#fff",
      border: `2px solid ${LINE}`,
    },
  },
  statusEventComplete: {
    "&::before": {
      background: INK,
      borderColor: INK,
    },
  },
  statusEventFailed: {
    "&::before": {
      background: "#B91C1C",
      borderColor: "#B91C1C",
    },
  },
  statusEventTitle: {
    fontSize: 13.5,
    fontWeight: 700,
    color: INK,
    margin: 0,
    lineHeight: 1.3,
  },
  statusEventTitleFailed: { color: "#B91C1C" },
  statusEventTitleIdle:   { color: MUTED },
  statusEventWhen: { fontSize: 11.5, color: MUTED, marginTop: 2, fontVariantNumeric: "tabular-nums" },
  statusEventMeta: { fontSize: 12.5, color: INK2, marginTop: 4, lineHeight: 1.45 },
  statusEventMetaIdle: { color: MUTED },

  /* ── error cells ── */
  cmsNumError: { color: "#B91C1C" },
  cmsValError: {
    border: "1.5px dashed #F87171",
    borderRadius: 4,
    padding: "2px 6px",
    background: "rgba(252,165,165,0.18)",
    outline: "none",
    cursor: "text",
    minWidth: 20,
    animation: "rjCaretBlink 1.05s steps(1, end) infinite",
    "@keyframes rjCaretBlink": {
      "0%, 50%": { boxShadow: "inset 1px 0 0 0 #B91C1C" },
      "51%, 100%": { boxShadow: "inset 1px 0 0 0 transparent" },
    },
    "&:hover": { background: "rgba(252,165,165,0.28)" },
    "&:focus": {
      background: "#fff",
      border: "1.5px solid #DC2626",
      animation: "none",
      boxShadow: "0 0 0 3px rgba(248,113,113,0.22)",
    },
  },
  cmsInputError: {
    fontSize: 12,
    color: INK,
    fontWeight: 500,
    fontFamily: "'Courier New', monospace",
    lineHeight: 1.4,
    width: "100%",
    minHeight: 22,
    boxSizing: "border-box",
    border: "1.5px dashed #F87171",
    borderRadius: 4,
    padding: "2px 6px",
    background: "rgba(252,165,165,0.18)",
    outline: "none",
    cursor: "text",
    display: "block",
    "&:focus": {
      background: "#fff",
      border: "1.5px solid #DC2626",
      boxShadow: "0 0 0 3px rgba(248,113,113,0.22)",
    },
  },
  cmsInputErrorInline: {
    fontSize: 12,
    color: INK,
    fontWeight: 500,
    fontFamily: "'Courier New', monospace",
    boxSizing: "border-box",
    border: "1.5px dashed #F87171",
    borderRadius: 4,
    padding: "2px 6px",
    background: "rgba(252,165,165,0.18)",
    outline: "none",
    cursor: "text",
    display: "inline-block",
    width: "100%",
    minWidth: 40,
    minHeight: 22,
    "&:focus": {
      background: "#fff",
      border: "1.5px solid #DC2626",
      boxShadow: "0 0 0 3px rgba(248,113,113,0.22)",
    },
  },
  billProviderFields: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  billProviderField: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  billProviderSubLabel: {
    fontSize: 9,
    fontWeight: 700,
    color: "#B91C1C",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  cmsErrorCell: {
    position: "relative",
  },
  errTooltipTitle: {
    fontSize: 10.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "#B91C1C",
    marginBottom: 4,
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  errTooltipBox: {
    fontSize: 11.5,
    fontWeight: 700,
    color: INK,
    marginBottom: 4,
  },
  errTooltipMsg: {
    fontSize: 12,
    color: INK2,
    lineHeight: 1.5,
    margin: 0,
  },

  /* ── misc ── */
  empty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "60px 20px",
    gap: 16,
  },
}));

/* ─── helpers ─────────────────────────────────────────────────────────────── */
const diagStr = (d) => {
  if (!d) return null;
  if (typeof d === "string") return d;
  if (typeof d === "object") return d.code || d.name || null;
  return String(d);
};
const fmtMoney = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const fmtDate = (d) => (d ? moment(d).format("MM/DD/YYYY") : "·");
const num = (v) => parseFloat(v) || 0;
const claimAmount = (c) =>
  c?.total_charge != null && c?.total_charge !== ""
    ? num(c.total_charge)
    : (c?.charge || []).reduce((t, ch) => t + num(ch?.charge), 0);
const ageFrom = (dob) => {
  if (!dob) return null;
  const a = moment().diff(moment(dob), "years");
  return Number.isFinite(a) && a >= 0 && a < 130 ? a : null;
};
const initials = (f, l) =>
  `${(f || "")[0] || ""}${(l || "")[0] || ""}`.toUpperCase() || "?";
const clientImg = (c, allPatients) => {
  try {
    const pats = allPatients || [];
    const matched = pats.find(
      (p) =>
        p?.form1?.firstName?.toLowerCase() === (c?.pat_name_f || "").toLowerCase() &&
        p?.form1?.lastName?.toLowerCase() === (c?.pat_name_l || "").toLowerCase()
    );
    const raw = matched?.imageurl;
    if (!raw) return null;
    return decrypt(raw) || null;
  } catch {
    return null;
  }
};
const memberId = (c) => c?.ins_number || "·";
const insuredName = (c) =>
  [c?.ins_name_l, c?.ins_name_f, c?.ins_name_m].filter(Boolean).join(", ") || "SAME";
const insuredAddress = (c) => {
  const cityLine = [c?.ins_city, c?.ins_state].filter(Boolean).join(", ");
  const lines = [
    c?.ins_addr_1,
    c?.ins_addr_2,
    [cityLine, c?.ins_zip].filter(Boolean).join(" "),
  ].filter(Boolean);
  return lines.length ? lines.join("\n") : "SAME";
};
const cmsName = (c) =>
  (
    [c?.pat_name_l, c?.pat_name_f, c?.pat_name_m].filter(Boolean).join(", ") ||
    "·"
  ).toUpperCase();
const patientAddress = (c) => {
  const cityLine = [c?.pat_city, c?.pat_state].filter(Boolean).join(", ");
  const lines = [
    c?.pat_addr_1,
    c?.pat_addr_2,
    [cityLine, c?.pat_zip].filter(Boolean).join(" "),
  ].filter(Boolean);
  return lines.length ? lines : ["·"];
};
const diagnoses = (c) => {
  if (Array.isArray(c?.diagnosis) && c.diagnosis.length)
    return c.diagnosis.filter(Boolean);
  return [];
};
const insuranceTypes = ["Medicare", "Medicaid", "Tricare", "Champva", "Group Health", "FECA", "Other"];
const insuranceLine = (c) => {
  const p = (c?.payer_name || "").toLowerCase();
  let active = "Other";
  if (p.includes("medicare")) active = "Medicare";
  else if (p.includes("medicaid")) active = "Medicaid";
  else if (p.includes("tricare")) active = "Tricare";
  else if (p.includes("champva")) active = "Champva";
  return insuranceTypes.map((t) => `${t === active ? "☑" : "☐"} ${t}`).join("  ");
};
const refName = (c) =>
  [c?.ref_name_l, c?.ref_name_f, c?.ref_name_m].filter(Boolean).join(", ") || "·";
const facilityInfo = (c) => {
  const cityLine = [c?.facility_city, c?.facility_state].filter(Boolean).join(", ");
  const parts = [
    c?.facility_name,
    c?.facility_addr_1,
    [cityLine, c?.facility_zip].filter(Boolean).join(" "),
    c?.facility_npi ? `NPI ${c.facility_npi}` : null,
  ].filter(Boolean);
  return parts.length ? parts : ["·"];
};
const billingProviderInfo = (c) => {
  const cityLine = [c?.bill_city, c?.bill_state].filter(Boolean).join(", ");
  const parts = [
    c?.bill_name,
    c?.bill_addr_1,
    c?.bill_addr_2,
    [cityLine, c?.bill_zip].filter(Boolean).join(" "),
    c?.bill_npi ? `NPI ${c.bill_npi}` : (c?.bill_id ? `ID ${c.bill_id}` : null),
    c?.bill_phone ? `Ph ${c.bill_phone}` : null,
  ].filter(Boolean);
  return parts.length ? parts : ["·"];
};
const signingProvider = (c) =>
  [c?.chg_prov_name_l, c?.chg_prov_name_f, c?.chg_prov_name_m].filter(Boolean).join(", ") ||
  c?.prov_name_m || "·";
const conditionRelated = (c) => {
  const emp = c?.employment_related === "Y" ? "☑" : "☐";
  const auto = c?.auto_accident === "Y" ? "☑" : "☐";
  const other = c?.other_accident === "Y" ? "☑" : "☐";
  return `a. ${emp} Employ.  b. ${auto} Auto${c?.auto_accident_state ? ` (${c.auto_accident_state})` : ""}  c. ${other} Other`;
};
const rejectionReason = (c) => {
  if (Array.isArray(c?.errors) && c.errors.length) {
    const msg = c.errors
      .map((e) => e?.message)
      .filter((m) => m && m.toLowerCase() !== "acknowledged")
      .join("; ");
    if (msg) return msg;
  }
  return c?.rejectionReason || c?.error || "Unknown Error";
};

/* ─── status popup events ─────────────────────────────────────────────────── */
const buildStatusEvents = (submittedDate, rejectedDate) => [
  { state: "complete", title: "Submitted",                     when: submittedDate, meta: "Claim built locally and sent to ClaimMD clearinghouse." },
  { state: "complete", title: "Acknowledged by clearinghouse", when: submittedDate, meta: "ClaimMD accepted the 837P file (ACK 999 received)." },
  { state: "complete", title: "Forwarded to payer",            when: submittedDate, meta: "File handed off to payer for adjudication." },
  { state: "complete", title: "In review",                     when: rejectedDate,  meta: "Payer began adjudication on the claim line items." },
  { state: "complete", title: "Adjudicated",                   when: rejectedDate,  meta: "Payer completed adjudication and returned a final disposition." },
  { state: "failed",   title: "Rejected",                      when: rejectedDate,  meta: "Review flagged fields and correct errors below, then resubmit." },
  { state: "idle",     title: "Resubmission pending",          when: "Awaiting your action", meta: "Save & Resubmit once you have corrected the flagged fields." },
];

/* ─── error field mapping ─────────────────────────────────────────────────── */
/*
 * Errors come from API as { message: "...", fields?: "field_name" }.
 * We match by fields property first, then by message text keywords.
 */
const errMsg = (e) =>
  (e?.message || e?.msg || (typeof e === "string" ? e : "")).toLowerCase();

const getFieldErrors = (claim, fieldKey) => {
  if (!Array.isArray(claim?.errors)) return [];
  return claim.errors.filter((e) => {
    if (e?.fields === fieldKey) return true;
    const m = errMsg(e);
    switch (fieldKey) {
      case "bill_npi":      return (m.includes("npi") && (m.includes("billing") || m.includes("rendering") || m.includes("invalid")));
      case "bill_id":       return m.includes("billing provider id") || m.includes("billing id") || (m.includes("provider id") && m.includes("billing"));
      case "bill_phone":    return m.includes("bill phone") || (m.includes("phone") && m.includes("invalid"));
      case "bill_name":     return m.includes("billing provider name");
      case "bill_taxonomy": return m.includes("taxonomy");
      case "bill_taxid":    return m.includes("tax id");
      case "prov_taxid":    return m.includes("tax id");
      case "ins_number":    return m.includes("member id") || m.includes("subscriber id") || m.includes("subscriber member");
      case "pat_addr_1":    return m.includes("patient address") || m.includes("patient street") || (m.includes("address") && m.includes("patient"));
      case "ins_addr_1":    return m.includes("insured address") || m.includes("subscriber address") || (m.includes("address") && m.includes("insured"));
      case "payer_addr_1":  return m.includes("payer address") || (m.includes("address") && m.includes("payer"));
      case "prior_auth":    return m.includes("prior auth") || m.includes("authorization number");
      case "diagnosis":     return (m.includes("diagnosis pointer") || m.includes("icd-10")) && !m.includes("procedure");
      case "payer_name":    return m.includes("payer name") || m.includes("payer id");
      default:              return false;
    }
  });
};

const getLineErrors = (claim, ch, colKey, lineIdx) => {
  const claimErrs = Array.isArray(claim?.errors) ? claim.errors : [];
  const lineErrs  = Array.isArray(ch?.errors)    ? ch.errors    : [];
  const n = lineIdx != null ? lineIdx + 1 : null;
  return [...claimErrs, ...lineErrs].filter((e) => {
    if (e?.fields === colKey) return true;
    const m = errMsg(e);
    switch (colKey) {
      case "from_date":
        if (n != null && (e?.fields === `from_date_${n}` || e?.fields === `from_date`)) return true;
        if (m.includes("from date") || m.includes("from_date")) return true;
        return false;
      case "thru_date":
        if (n != null && (e?.fields === `thru_date_${n}` || e?.fields === `thru_date`)) return true;
        if (m.includes("thru date") || m.includes("thru_date")) return true;
        return false;
      case "proc_code": return m.includes("procedure code") || m.includes("ncci") || m.includes("cpt") || m.includes("hcpcs");
      case "units":     return m.includes("unit") && !m.includes("place") && !m.includes("billing");
      case "pos":       return m.includes("place of service");
      case "mod":       return m.includes("modifier");
      case "dates":     return m.includes("date of service") || m.includes("service date") || m.includes("date is in the future") || m.includes("date is invalid");
      default:          return false;
    }
  });
};

/* ─── error cell ─────────────────────────────────────────────────────────── */
const ErrorCell = ({ classes, errors, numLabel, valContent, cx, extraValClass, value, onChange, readOnly }) => {
  const hasErr = errors && errors.length > 0;
  const err = hasErr ? errors[0] : null;

  const cellContent = (
    <div className={cx(classes.cmsCell, extraValClass, hasErr && classes.cmsErrorCell)}>
      <div className={cx(classes.cmsNum, hasErr && classes.cmsNumError)}>
        {numLabel}{hasErr ? " ★" : ""}
      </div>
      {hasErr && !readOnly ? (
        <input
          className={classes.cmsInputError}
          value={value ?? ""}
          onChange={(e) => onChange?.(e.target.value)}
        />
      ) : (
        <div className={cx(classes.cmsVal, hasErr && classes.cmsValError)}>
          {valContent}
        </div>
      )}
    </div>
  );

  if (!hasErr) return cellContent;

  return (
    <Tooltip
      label={
        <div>
          <div className={classes.errTooltipTitle}>
            <LuAlertTriangle size={12} /> Error
          </div>
          {err.fields && <div className={classes.errTooltipBox}>{err.fields}</div>}
          <p className={classes.errTooltipMsg}>{err.message || ""}</p>
        </div>
      }
      position="right"
      withArrow
      withinPortal
      arrowSize={10}
      openDelay={100}
      closeDelay={100}
      multiline
      width={280}
      styles={{
        tooltip: {
          border: "1px solid #FCA5A5",
          borderLeft: "3px solid #DC2626",
          borderRadius: 8,
          boxShadow: "0 12px 28px rgba(220,38,38,0.18)",
          padding: "10px 12px",
          fontFamily: "Inter, sans-serif",
          background: "#fff",
          color: "inherit",
        },
        arrow: { border: "1px solid #FCA5A5", background: "#fff" },
      }}
    >
      {cellContent}
    </Tooltip>
  );
};

/* ─── component ──────────────────────────────────────────────────────────── */
const BillingRejected = () => {
  const { classes, cx } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useSelector((state) => state?.profile);
  const { allBilling, loading } = useSelector((state) => state?.billing);
  const { patients } = useSelector((state) => state?.patient);

  const [selectedIdx, setSelectedIdx] = useState(0);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [cmsLoading, setCmsLoading] = useState(false);
  const paperRef = useRef(null);
  const [statusOpen, setStatusOpen] = useState(false);
  const [fieldTouched, setFieldTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [draftClaim, setDraftClaim] = useState({});
  const [localBilling, setLocalBilling] = useState(null);

  // If navigated from main table with a specific claim → show only that one
  const navClaim = location.state?.claim;
  const navBatch = location.state?.batch;
  const isNavigation = !!navClaim;

  const fetchRejected = async () => {
    const data = await getAllBillingModal({ page: "all", status: "R" });
    if (data) setLocalBilling(data);
  };

  useEffect(() => {
    if (!isNavigation) fetchRejected();
    getAllPatients("all", true, true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rejectedClaims = useMemo(() => {
    if (isNavigation) return [{ claim: navClaim, batch: navBatch }];
    const billing = localBilling ?? allBilling;
    const out = [];
    (billing?.Billing || []).forEach((batch) => {
      (batch?.billingForm?.claim || []).forEach((claim) => {
        if (claim?.deleted === true || claim?.status !== "R") return;
        out.push({ claim, batch });
      });
    });
    return out;
  }, [localBilling, allBilling, isNavigation]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadCms = async () => {
    if (!paperRef.current || cmsLoading) return;
    try {
      setCmsLoading(true);
      const claimId = rejectedClaims[selectedIdx]?.claim?.claimmd_id || "CMS-1500";
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");

      const canvas = await html2canvas(paperRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      const MARGIN = 20;
      const A4_W = 595.28;
      const A4_H = 841.89;
      const printW = A4_W - MARGIN * 2;
      const printH = (canvas.height * printW) / canvas.width;

      const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });

      if (printH <= A4_H - MARGIN * 2) {
        // Fits one page
        doc.addImage(canvas.toDataURL("image/png"), "PNG", MARGIN, MARGIN, printW, printH);
      } else {
        // Multi-page: slice canvas into A4-height chunks
        const pageContentH = A4_H - MARGIN * 2;
        const totalPages = Math.ceil(printH / pageContentH);

        for (let page = 0; page < totalPages; page++) {
          if (page > 0) doc.addPage();
          const srcY = Math.round((page * pageContentH * canvas.width) / printW);
          const srcH = Math.min(
            Math.round((pageContentH * canvas.width) / printW),
            canvas.height - srcY
          );
          if (srcH <= 0) break;

          const slice = document.createElement("canvas");
          slice.width = canvas.width;
          slice.height = srcH;
          slice.getContext("2d").drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);

          const sliceH = (srcH * printW) / canvas.width;
          doc.addImage(slice.toDataURL("image/png"), "PNG", MARGIN, MARGIN, printW, sliceH);
        }
      }

      doc.save(`CMS-1500_${claimId}.pdf`);
    } catch (e) {
      console.error("CMS-1500 download error:", e);
    } finally {
      setCmsLoading(false);
    }
  };

  const selectedClaimId = rejectedClaims[selectedIdx]?.claim?._id;

  useEffect(() => {
    if (selectedIdx > rejectedClaims.length - 1) { setSelectedIdx(0); return; }
    setFieldTouched(false);
    const claim = rejectedClaims[selectedIdx]?.claim;
    if (claim) setDraftClaim({ ...claim, charge: (claim.charge || []).map((ch) => ({ ...ch })) });
  }, [selectedClaimId, selectedIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = rejectedClaims[selectedIdx];

  const handleDelete = async () => {
    if (!selected) return;
    const willDelete = await swal({
      title: "Are you sure?",
      text: "This action cannot be undone.",
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Delete"],
    });
    if (willDelete) {
      await removeBilling({
        id: selected.batch?._id,
        claimid: selected.claim?.claimmd_id,
      });
      await fetchRejected();
      navigate("/home/billing");
    }
  };

  const updateDraft = (key, value) => {
    setDraftClaim((prev) => ({ ...prev, [key]: value }));
    setFieldTouched(true);
  };

  const updateDraftCharge = (lineIdx, key, value) => {
    setDraftClaim((prev) => ({
      ...prev,
      charge: (prev.charge || []).map((ch, i) =>
        i === lineIdx ? { ...ch, [key]: value } : ch,
      ),
    }));
    setFieldTouched(true);
  };

  const makeCellErr = (errs, content, colKey, lineIdx) => {
    if (errs.length === 0) return content;
    let inputValue;
    if (colKey === "mods") {
      const ch = draftClaim?.charge?.[lineIdx] || {};
      inputValue = [ch.mod1, ch.mod2, ch.mod3, ch.mod4].filter(Boolean).join(" ");
      if (!inputValue) inputValue = content === "·" ? "" : content;
    } else {
      const draftVal = draftClaim?.charge?.[lineIdx]?.[colKey];
      const origVal  = selected?.claim?.charge?.[lineIdx]?.[colKey];
      inputValue = (draftVal != null && draftVal !== origVal)
        ? draftVal
        : (content === "·" ? "" : content);
    }
    return (
      <input
        className={classes.cmsInputErrorInline}
        value={inputValue}
        onChange={(e) => {
          if (colKey === "mods") {
            const parts = e.target.value.split(/\s+/).filter(Boolean);
            setDraftClaim((prev) => ({
              ...prev,
              charge: (prev.charge || []).map((ch, i) =>
                i === lineIdx
                  ? { ...ch, mod1: parts[0] || "", mod2: parts[1] || "", mod3: parts[2] || "", mod4: parts[3] || "" }
                  : ch,
              ),
            }));
            setFieldTouched(true);
          } else {
            updateDraftCharge(lineIdx, colKey, e.target.value);
          }
        }}
        title={errs[0]?.message || "Error"}
      />
    );
  };

  const handleSaveAndResubmit = async () => {
    if (!selected || !Object.keys(draftClaim).length) return;
    setSubmitting(true);
    try {
      const { batch } = selected;
      const payload = {
        file: encrypt(draftClaim),
        id: batch._id,
        claimid: draftClaim.claimmd_id,
        filename: draftClaim?.prov_name_m,
        claimmongoid: draftClaim?._id,
      };
      const res = await editBilling(payload);
      if (res && res.status === 200) {
        await getAllBillingModal();
        navigate("/home/billing");
      }
    } catch (err) {
      console.error("Save error", err);
    } finally {
      setSubmitting(false);
      setFieldTouched(false);
    }
  };

  /* ── left list ── */
  const renderList = () => (
    <div className={classes.rjList}>
      <div className={classes.rjListHead}>
        <h3>
          {rejectedClaims.length} rejected claim
          {rejectedClaims.length !== 1 ? "s" : ""}
        </h3>
      </div>
      <div className={classes.listScroll}>
        {rejectedClaims.map(({ claim, batch }, i) => (
          <button
            key={`${claim?.claimmd_id || "clm"}-${i}`}
            type="button"
            className={cx(classes.rjCard, i === selectedIdx && classes.rjCardSelected)}
            onClick={() => setSelectedIdx(i)}
          >
            <div className={classes.rjCardRow1}>
              <span className={classes.rjCardId}>
                {claim?.claimmd_id || `CLM-${i + 1}`}
              </span>
              <span className={classes.rjCardAmt}>{fmtMoney(claimAmount(claim))}</span>
            </div>
            <div className={classes.rjCardName}>
              {claim?.pat_name_f} {claim?.pat_name_l}
            </div>
            <div className={classes.rjCardPayer}>
              {claim?.payer_name || "—"} · rejected{" "}
              {batch?.createdAt ? moment(batch.createdAt).format("MMM DD") : "—"}
              <span className={classes.cardProvider}>
                <b>Provider:</b>{" "}
                {[claim?.bill_name, claim?.chg_prov_name_l].filter(Boolean).join(" / ") || "—"}
              </span>
            </div>
            <div className={classes.rjCardReason}>
              <b>Reason:</b> {rejectionReason(claim)}
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  /* ── right panel ── */
  const renderRight = () => {
    if (!selected) return null;
    const { claim, batch } = selected;
    const img = clientImg(claim, patients?.patients);
    const age = ageFrom(claim?.pat_dob);
    const metaLine = `DOB ${fmtDate(claim?.pat_dob)}${age != null ? ` · ${age}` : ""} · Member ${memberId(claim)}`;
    const rejectedDate = batch?.createdAt ? moment(batch.createdAt).format("MM/DD/YYYY") : "—";
    const statusEvents = buildStatusEvents(
      batch?.createdAt ? moment(batch.createdAt).format("MM/DD/YYYY hh:mm A") : "—",
      batch?.createdAt ? moment(batch.createdAt).format("MM/DD/YYYY") : "—"
    );
    const lines = draftClaim?.charge || claim?.charge || [];
    const total = claimAmount(claim);
    const dx = diagnoses(claim);

    return (
      <div>
        {/* page id */}
        <div className={classes.pageId}>
          <h2>
            Rejected Claims
            <span className={classes.countBadge}>{rejectedClaims.length}</span>
          </h2>
        </div>

        {/* status trigger */}
        <button
          type="button"
          className={classes.statusTrigger}
          onClick={() => setStatusOpen(true)}
        >
          <span className={classes.statusDot} />
          <span className={classes.statusLabel}>Claim status</span>
          <span>Rejected · {rejectedDate}</span>
          <span style={{ opacity: 0.55, display: "inline-flex", alignItems: "center" }}>
            <LuChevronRight size={12} />
          </span>
        </button>

        {/* cms form */}
        <div className={classes.cmsForm} id="rejected-cms-form">
          {/* head */}
          <div className={classes.cmsFormHead}>
            <div className={classes.headPatient}>
              <div
                className={classes.avatar}
                style={img ? { backgroundImage: `url('${img}')` } : undefined}
              >
                {!img && initials(claim?.pat_name_f, claim?.pat_name_l)}
              </div>
              <div className={classes.headInfo}>
                <div className={classes.headName}>
                  {claim?.pat_name_f} {claim?.pat_name_l}
                </div>
                <div className={classes.headMeta}>{metaLine}</div>
              </div>
            </div>

            <h3 className={classes.headTitle}>
              <button
                type="button"
                className={classes.cmsNavArrow}
                disabled={selectedIdx === 0}
                onClick={() => setSelectedIdx((p) => Math.max(0, p - 1))}
                aria-label="Previous rejected claim"
              >
                <LuChevronLeft />
              </button>
              <span className={classes.claimIdMono}>
                {claim?.claimmd_id || "—"}
              </span>
              <span>CMS-1500 form</span>
              <button
                type="button"
                className={classes.cmsNavArrow}
                disabled={selectedIdx >= rejectedClaims.length - 1}
                onClick={() =>
                  setSelectedIdx((p) => Math.min(rejectedClaims.length - 1, p + 1))
                }
                aria-label="Next rejected claim"
              >
                <LuChevronRight />
              </button>
            </h3>

            <div className={classes.headActions}>
              <button
                type="button"
                className={classes.downloadBtn}
                title="Download CMS-1500"
                aria-label="Download CMS-1500"
                onClick={downloadCms}
                disabled={cmsLoading}
              >
                {cmsLoading ? "…" : <LuDownload />}
              </button>
            </div>
          </div>

          {/* paper */}
          <div className={classes.paper} ref={paperRef}>
            <div className={classes.titleBar}>
              <span>Health Insurance Claim Form · CMS-1500</span>
            </div>

            {/* ── GRID 1 (rows 1-13) ── */}
            <div className={classes.cmsGrid}>
              {/* row 1 */}
              <div className={classes.cmsRow}>
                <div className={cx(classes.cmsCell, classes.flex2)}>
                  <div className={classes.cmsNum}>1. Insurance type</div>
                  <div className={classes.cmsVal}>{insuranceLine(claim)}</div>
                </div>
                <ErrorCell
                  classes={classes} cx={cx}
                  errors={getFieldErrors(claim, "ins_number")}
                  numLabel="1a. Insured's ID number"
                  valContent={memberId(claim)}
                  value={draftClaim?.ins_number ?? (claim?.ins_number || "")}
                  onChange={(v) => updateDraft("ins_number", v)}
                />
              </div>
              {/* payer info row */}
              <div className={classes.cmsRow}>
                <div className={cx(classes.cmsCell, classes.flex2)}>
                  <div className={classes.cmsNum}>Payer</div>
                  <div className={classes.cmsVal}>{claim?.payer_name || "·"}</div>
                </div>
                <ErrorCell
                  classes={classes} cx={cx}
                  extraValClass={classes.flex2}
                  errors={getFieldErrors(claim, "payer_addr_1")}
                  numLabel="Payer address"
                  valContent={(() => {
                    const cityLine = [claim?.payer_city, claim?.payer_state].filter(Boolean).join(", ");
                    const lines = [claim?.payer_addr_1, [cityLine, claim?.payer_zip].filter(Boolean).join(" ")].filter(Boolean);
                    return lines.length ? lines.map((ln, i) => <div key={i}>{ln}</div>) : "·";
                  })()}
                  value={draftClaim?.payer_addr_1 ?? (claim?.payer_addr_1 || "")}
                  onChange={(v) => updateDraft("payer_addr_1", v)}
                />
              </div>
              {/* row 2 */}
              <div className={classes.cmsRow}>
                <div className={cx(classes.cmsCell, classes.flex2)}>
                  <div className={classes.cmsNum}>2. Patient's name (Last, First, MI)</div>
                  <div className={classes.cmsVal}>{cmsName(claim)}</div>
                </div>
                <div className={classes.cmsCell}>
                  <div className={classes.cmsNum}>3. Birth date · Sex</div>
                  <div className={classes.cmsVal}>
                    {fmtDate(claim?.pat_dob)}&nbsp; M {claim?.pat_sex === "M" ? "☑" : "☐"}&nbsp; F {claim?.pat_sex === "F" ? "☑" : "☐"}
                  </div>
                </div>
                <div className={cx(classes.cmsCell, classes.flex2)}>
                  <div className={classes.cmsNum}>4. Insured's name</div>
                  <div className={classes.cmsVal}>{insuredName(claim)}</div>
                </div>
              </div>
              {/* row 3 */}
              <div className={classes.cmsRow}>
                <ErrorCell
                  classes={classes} cx={cx}
                  extraValClass={classes.flex3}
                  errors={getFieldErrors(claim, "pat_addr_1")}
                  numLabel="5. Patient's address"
                  valContent={
                    <>
                      {patientAddress(claim).map((ln, i) => <div key={i}>{ln}</div>)}
                      {claim?.pat_phone ? <div>{claim.pat_phone}</div> : null}
                    </>
                  }
                  value={draftClaim?.pat_addr_1 ?? (claim?.pat_addr_1 || "")}
                  onChange={(v) => updateDraft("pat_addr_1", v)}
                />
                <div className={classes.cmsCell}>
                  <div className={classes.cmsNum}>6. Relationship to insured</div>
                  <div className={classes.cmsVal}>
                    {claim?.pat_rel === "18" || !claim?.pat_rel ? "☑ Self" : "☐ Self"}{" "}
                    {claim?.pat_rel === "01" ? "☑ Spouse" : "☐ Spouse"}{" "}
                    {claim?.pat_rel === "19" ? "☑ Child" : "☐ Child"}{" "}
                    {!["18","01","19",""].includes(claim?.pat_rel || "") && claim?.pat_rel ? "☑ Other" : "☐ Other"}
                  </div>
                </div>
                <ErrorCell
                  classes={classes} cx={cx}
                  extraValClass={classes.flex2}
                  errors={getFieldErrors(claim, "ins_addr_1")}
                  numLabel="7. Insured's address"
                  valContent={
                    <>
                      {insuredAddress(claim).split("\n").map((ln, i) => <div key={i}>{ln}</div>)}
                      {claim?.ins_phone ? <div>{claim.ins_phone}</div> : null}
                    </>
                  }
                  value={draftClaim?.ins_addr_1 ?? (claim?.ins_addr_1 || "")}
                  onChange={(v) => updateDraft("ins_addr_1", v)}
                />
              </div>
              {/* row 4 */}
              <div className={classes.cmsRow}>
                <div className={cx(classes.cmsCell, classes.flex2)}>
                  <div className={classes.cmsNum}>9. Other insured's name</div>
                  <div className={classes.cmsVal}>
                    {[claim?.other_ins_name_l, claim?.other_ins_name_f, claim?.other_ins_name_m]
                      .filter(Boolean).join(", ") || "·"}
                    {claim?.other_ins_number ? <div>{claim.other_ins_number}</div> : null}
                  </div>
                </div>
                <div className={cx(classes.cmsCell, classes.flex2)}>
                  <div className={classes.cmsNum}>10. Condition related to</div>
                  <div className={classes.cmsVal}>{conditionRelated(claim)}</div>
                </div>
                <div className={cx(classes.cmsCell, classes.flex2)}>
                  <div className={classes.cmsNum}>11. Insured's policy / group #</div>
                  <div className={classes.cmsVal}>{claim?.ins_group || "·"}</div>
                  <div className={classes.cmsNum} style={{ marginTop: 4 }}>11a. Insured DOB · Sex</div>
                  <div className={classes.cmsVal}>
                    {fmtDate(claim?.ins_dob)}&nbsp; M {claim?.ins_sex === "M" ? "☑" : "☐"}&nbsp; F {claim?.ins_sex === "F" ? "☑" : "☐"}
                  </div>
                  <div className={classes.cmsNum} style={{ marginTop: 4 }}>11c. Insurance plan name</div>
                  <div className={classes.cmsVal}>{claim?.ins_plan || "·"}</div>
                </div>
              </div>
              {/* row 5 */}
              <div className={classes.cmsRow}>
                <div className={cx(classes.cmsCell, classes.flex3)}>
                  <div className={classes.cmsNum}>12. Patient's signature</div>
                  <div className={classes.cmsVal}>SIGNATURE ON FILE</div>
                </div>
                <div className={cx(classes.cmsCell, classes.flex3)}>
                  <div className={classes.cmsNum}>13. Insured's authorization signature</div>
                  <div className={classes.cmsVal}>SIGNATURE ON FILE</div>
                </div>
              </div>
            </div>

            {/* ── GRID 2 (rows 14-23) ── */}
            <div className={classes.cmsGridMt}>
              {/* row 1 */}
              <div className={classes.cmsRow}>
                <div className={classes.cmsCell}>
                  <div className={classes.cmsNum}>14. Date of current illness / injury</div>
                  <div className={classes.cmsVal}>{fmtDate(claim?.cond_date)}</div>
                </div>
                <div className={classes.cmsCell}>
                  <div className={classes.cmsNum}>15. Other date</div>
                  <div className={classes.cmsVal}>·</div>
                </div>
                <div className={classes.cmsCell}>
                  <div className={classes.cmsNum}>16. Dates unable to work</div>
                  <div className={classes.cmsVal}>
                    {claim?.nowork_from_date
                      ? `From ${fmtDate(claim.nowork_from_date)}`
                      : "·"}
                    {claim?.nowork_to_date
                      ? ` To ${fmtDate(claim.nowork_to_date)}`
                      : ""}
                  </div>
                </div>
                <div className={classes.cmsCell}>
                  <div className={classes.cmsNum}>17. Referring provider</div>
                  <div className={classes.cmsVal}>
                    {refName(claim)}
                    {claim?.ref_npi ? <div>NPI {claim.ref_npi}</div> : null}
                    {claim?.ref_id ? <div>ID {claim.ref_id}</div> : null}
                  </div>
                </div>
              </div>
              {/* row 2 */}
              <div className={classes.cmsRow}>
                <div className={cx(classes.cmsCell, classes.flex2)}>
                  <div className={classes.cmsNum}>18. Hospitalization dates related to current services</div>
                  <div className={classes.cmsVal}>
                    {claim?.hosp_from_date
                      ? `From ${fmtDate(claim.hosp_from_date)}`
                      : "·"}
                    {claim?.hosp_thru_date
                      ? ` Through ${fmtDate(claim.hosp_thru_date)}`
                      : ""}
                  </div>
                </div>
                <div className={cx(classes.cmsCell, classes.flex2)}>
                  <div className={classes.cmsNum}>19. Additional claim information</div>
                  <div className={classes.cmsVal}>{claim?.narrative || "·"}</div>
                </div>
                <div className={classes.cmsCell}>
                  <div className={classes.cmsNum}>20. Outside lab? $ Charges</div>
                  <div className={classes.cmsVal}>☐ Yes ☑ No</div>
                </div>
              </div>
              {/* row 3 */}
              <div className={classes.cmsRow}>
                <div className={cx(classes.cmsCell, classes.flex3)}>
                  <div className={classes.cmsNum}>21. Diagnosis or nature of illness / injury (ICD-10)</div>
                  <div className={classes.cmsVal}>
                    {dx.length
                      ? dx
                          .slice(0, 12)
                          .map((d, i) => `${String.fromCharCode(65 + i)}. ${diagStr(d)}`)
                          .join("   ")
                      : "·"}
                  </div>
                </div>
                <div className={classes.cmsCell}>
                  <div className={classes.cmsNum}>22. Resubmission code / original ref. #</div>
                  <div className={classes.cmsVal}>
                    {claim?.frequency_code || "·"}
                    {claim?.referral_number ? <div>{claim.referral_number}</div> : null}
                  </div>
                </div>
                <ErrorCell
                  classes={classes} cx={cx}
                  errors={getFieldErrors(claim, "prior_auth")}
                  numLabel="23. Prior authorization #"
                  valContent={claim?.prior_auth || "·"}
                  value={draftClaim?.prior_auth ?? (claim?.prior_auth || "")}
                  onChange={(v) => updateDraft("prior_auth", v)}
                />

              </div>
            </div>

            {/* ── CMS SERVICES TABLE ── */}
            <div className={classes.cmsServices}>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 30 }}>#</th>
                    <th style={{ width: 90 }}>24A · From</th>
                    <th style={{ width: 90 }}>24A · To</th>
                    <th style={{ width: 36 }}>B<br />POS</th>
                    <th style={{ width: 90 }}>D · CPT/HCPCS</th>
                    <th style={{ width: 90 }}>D · Modifiers</th>
                    <th style={{ width: 60 }}>E · Dx Ptr</th>
                    <th style={{ width: 90 }}>F · Charges</th>
                    <th style={{ width: 50 }}>G · Units</th>
                    <th style={{ width: 120 }}>J · Rendering NPI</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length ? (
                    lines.map((ch, i) => {
                      const errFromDates = getLineErrors(claim, ch, "from_date", i);
                      const errThruDates = getLineErrors(claim, ch, "thru_date", i);
                      const errPos       = getLineErrors(claim, ch, "pos", i);
                      const errProc      = getLineErrors(claim, ch, "proc_code", i);
                      const errMod       = getLineErrors(claim, ch, "mod", i);
                      const errUnits     = getLineErrors(claim, ch, "units", i);
                      const cellErr = (errs, content, colKey) => makeCellErr(errs, content, colKey, i);
                      return (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          <td>{cellErr(errFromDates, ch?.from_date ? moment(ch.from_date).format("MM/DD/YY") : "·", "from_date")}</td>
                          <td>{cellErr(errThruDates, ch?.thru_date ? moment(ch.thru_date).format("MM/DD/YY") : "·", "thru_date")}</td>
                          <td>{cellErr(errPos, ch?.place_of_service || "·", "place_of_service")}</td>
                          <td>{cellErr(errProc, ch?.proc_code || "·", "proc_code")}</td>
                          <td>{cellErr(errMod, [ch?.mod1, ch?.mod2, ch?.mod3, ch?.mod4].filter(Boolean).join(" ") || "·", "mods")}</td>
                          <td>{ch?.diag_ref || "·"}</td>
                          <td>{fmtMoney(num(ch?.charge))}</td>
                          <td>{cellErr(errUnits, ch?.units || "·", "units")}</td>
                          <td>{ch?.rendering_npi || claim?.prov_npi || claim?.prov_id || claim?.bill_npi || claim?.bill_id || "·"}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={10}>·</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ── GRID 3 (rows 25-33) ── */}
            <div className={classes.cmsGridMt}>
              {/* row 1 */}
              <div className={classes.cmsRow}>
                {(() => {
                  const taxid     = claim?.bill_taxid || claim?.prov_taxid || "";
                  const taxidType = claim?.bill_taxid_type || claim?.prov_taxid_type || "";
                  const taxidKey  = claim?.bill_taxid ? "bill_taxid" : "prov_taxid";
                  const taxErrs   = [...getFieldErrors(claim, "bill_taxid"), ...getFieldErrors(claim, "prov_taxid")];
                  return (
                    <ErrorCell
                      classes={classes} cx={cx}
                      errors={taxErrs}
                      numLabel="25. Federal Tax ID"
                      valContent={
                        <>{taxid || "·"}{" "}
                        {taxidType === "S" ? "☐ EIN ☑ SSN" : "☑ EIN ☐ SSN"}</>
                      }
                      value={draftClaim?.[taxidKey] ?? taxid}
                      onChange={(v) => updateDraft(taxidKey, v)}
                    />
                  );
                })()}
                <div className={classes.cmsCell}>
                  <div className={classes.cmsNum}>26. Patient acct #</div>
                  <div className={classes.cmsVal}>{claim?.pat_acct || "·"}</div>
                </div>
                <div className={classes.cmsCell}>
                  <div className={classes.cmsNum}>27. Accept assignment?</div>
                  <div className={classes.cmsVal}>
                    {claim?.accept_assign === "N" ? "☐ Yes ☑ No" : "☑ Yes ☐ No"}
                  </div>
                </div>
                <div className={classes.cmsCell}>
                  <div className={classes.cmsNum}>28. Total charge</div>
                  <div className={classes.cmsVal} style={{ fontWeight: 700 }}>
                    {fmtMoney(total)}
                  </div>
                </div>
                <div className={classes.cmsCell}>
                  <div className={classes.cmsNum}>29. Amount paid</div>
                  <div className={classes.cmsVal}>$0.00</div>
                </div>
              </div>
              {/* row 2 */}
              <div className={classes.cmsRow}>
                <div className={cx(classes.cmsCell, classes.flex2)}>
                  <div className={classes.cmsNum}>31. Signature of physician / supplier</div>
                  <div className={classes.cmsVal}>
                    SIGNATURE ON FILE
                    {signingProvider(claim) !== "·" ? <div>{signingProvider(claim)}</div> : null}
                  </div>
                </div>
                <div className={cx(classes.cmsCell, classes.flex2)}>
                  <div className={classes.cmsNum}>32. Service facility location info</div>
                  <div className={classes.cmsVal}>
                    {facilityInfo(claim).map((ln, i) => (
                      <div key={i}>{ln}</div>
                    ))}
                  </div>
                </div>
                {(() => {
                  const errBillName     = getFieldErrors(claim, "bill_name");
                  const errBillNpi      = getFieldErrors(claim, "bill_npi");
                  const errBillId       = getFieldErrors(claim, "bill_id");
                  const errBillPhone    = getFieldErrors(claim, "bill_phone");
                  const errBillTaxonomy = getFieldErrors(claim, "bill_taxonomy");
                  const hasAnyBillErr   = errBillName.length > 0 || errBillNpi.length > 0 || errBillId.length > 0 || errBillPhone.length > 0 || errBillTaxonomy.length > 0;
                  return (
                    <div className={cx(classes.cmsCell, classes.flex2, hasAnyBillErr && classes.cmsErrorCell)}>
                      <div className={cx(classes.cmsNum, hasAnyBillErr && classes.cmsNumError)}>
                        33. Billing provider info & Ph #{hasAnyBillErr ? " ★" : ""}
                      </div>
                      {hasAnyBillErr ? (
                        <div className={classes.billProviderFields}>
                          {errBillName.length > 0 ? (
                            <div className={classes.billProviderField}>
                              <span className={classes.billProviderSubLabel}>Provider Name</span>
                              <input
                                className={classes.cmsInputError}
                                value={draftClaim?.bill_name ?? (claim?.bill_name || "")}
                                onChange={(e) => updateDraft("bill_name", e.target.value)}
                              />
                            </div>
                          ) : claim?.bill_name ? (
                            <div className={classes.cmsVal}>{claim.bill_name}</div>
                          ) : null}
                          {errBillNpi.length > 0 ? (
                            <div className={classes.billProviderField}>
                              <span className={classes.billProviderSubLabel}>NPI</span>
                              <input
                                className={classes.cmsInputError}
                                value={draftClaim?.bill_npi ?? (claim?.bill_npi || "")}
                                onChange={(e) => updateDraft("bill_npi", e.target.value)}
                              />
                            </div>
                          ) : claim?.bill_npi ? (
                            <div className={classes.cmsVal}>NPI {claim.bill_npi}</div>
                          ) : null}
                          {errBillId.length > 0 ? (
                            <div className={classes.billProviderField}>
                              <span className={classes.billProviderSubLabel}>Provider ID</span>
                              <input
                                className={classes.cmsInputError}
                                value={draftClaim?.bill_id ?? (claim?.bill_id || "")}
                                onChange={(e) => updateDraft("bill_id", e.target.value)}
                              />
                            </div>
                          ) : !claim?.bill_npi && claim?.bill_id ? (
                            <div className={classes.cmsVal}>ID {claim.bill_id}</div>
                          ) : null}
                          {errBillPhone.length > 0 ? (
                            <div className={classes.billProviderField}>
                              <span className={classes.billProviderSubLabel}>Phone</span>
                              <input
                                className={classes.cmsInputError}
                                value={draftClaim?.bill_phone ?? (claim?.bill_phone || "")}
                                onChange={(e) => updateDraft("bill_phone", e.target.value)}
                              />
                            </div>
                          ) : claim?.bill_phone ? (
                            <div className={classes.cmsVal}>Ph {claim.bill_phone}</div>
                          ) : null}
                          {errBillTaxonomy.length > 0 ? (
                            <div className={classes.billProviderField}>
                              <span className={classes.billProviderSubLabel}>Taxonomy</span>
                              <input
                                className={classes.cmsInputError}
                                value={draftClaim?.bill_taxonomy ?? (claim?.bill_taxonomy || "")}
                                onChange={(e) => updateDraft("bill_taxonomy", e.target.value)}
                              />
                            </div>
                          ) : claim?.bill_taxonomy ? (
                            <div className={classes.cmsVal}>{claim.bill_taxonomy}</div>
                          ) : null}
                        </div>
                      ) : (
                        <div className={classes.cmsVal}>
                          {billingProviderInfo(claim).map((ln, bi) => <div key={bi}>{ln}</div>)}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {/* foot */}
          <div className={classes.cmsFormFoot}>
            <button
              type="button"
              className={classes.deleteClaimBtn}
              onClick={handleDelete}
              aria-label="Delete claim"
            >
              <LuTrash2 />
            </button>
            <button
              type="button"
              className={classes.resubmitBtn}
              disabled={!fieldTouched || submitting}
              onClick={handleSaveAndResubmit}
            >
              {submitting ? "Saving…" : "Save & Resubmit"}
            </button>
          </div>
        </div>

        {/* status popup */}
        {statusOpen && (
          <div
            className={classes.statusPopupOverlay}
            onClick={(e) => {
              if (e.target === e.currentTarget) setStatusOpen(false);
            }}
          >
            <div className={classes.statusPopup}>
              <div className={classes.statusPopupHead}>
                <div>
                  <h3>Claim status · Rejected</h3>
                  <p className={classes.statusPopupSub}>Lifecycle of this claim, including each touchpoint.</p>
                </div>
                <button
                  type="button"
                  className={classes.statusPopupClose}
                  onClick={() => setStatusOpen(false)}
                  aria-label="Close status popup"
                >
                  <LuX />
                </button>
              </div>
              <div className={classes.statusPopupBody}>
                <div className={classes.statusEvents}>
                  {statusEvents.map((ev, i) => (
                    <div
                      key={i}
                      className={cx(
                        classes.statusEvent,
                        ev.state === "complete" && classes.statusEventComplete,
                        ev.state === "failed"   && classes.statusEventFailed,
                      )}
                    >
                      <p className={cx(classes.statusEventTitle, ev.state === "failed" && classes.statusEventTitleFailed, ev.state === "idle" && classes.statusEventTitleIdle)}>
                        {ev.title}
                      </p>
                      {ev.when && <div className={classes.statusEventWhen}>{ev.when}</div>}
                      {ev.meta && <div className={cx(classes.statusEventMeta, ev.state === "idle" && classes.statusEventMetaIdle)}>{ev.meta}</div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Box fluid className="content">
      <AppNavbar title="Billing" profile={profile} />
      <div style={{ flexDirection: "column" }} className="mainbillingcontainer">
        <ClaimToolbar />

        {/* back button */}
        <button
          type="button"
          className={classes.backBtn}
          onClick={() => navigate("/home/billing")}
        >
          <LuArrowLeft />
          Back to all claims
        </button>

        {loading ? (
          <TableLoader />
        ) : rejectedClaims.length === 0 ? (
          <div className={classes.empty}>
            <RecordNotFound />
            <p style={{ color: "#A8A8A8", fontFamily: "Inter" }}>
              No rejected claims found
            </p>
          </div>
        ) : (
          <div
            className={cx(
              classes.rejectedGrid,
              listCollapsed && classes.rejectedGridCollapsed,
            )}
          >
            {/* collapse arrow — absolutely positioned, not a grid item */}
            <button
              type="button"
              className={cx(
                classes.gridCollapseArrow,
                listCollapsed && classes.gridCollapseArrowCollapsed,
              )}
              onClick={() => setListCollapsed((v) => !v)}
              aria-label={listCollapsed ? "Show claims list" : "Hide claims list"}
            >
              <span className={classes.gcIcon}><LuChevronLeft /></span>
              {listCollapsed && <span className={classes.gcLabel}>Claim List</span>}
            </button>

            {/* list — always in DOM so grid column count stays stable; hidden via display:none */}
            <div className={listCollapsed ? classes.listHidden : undefined}>
              {renderList()}
            </div>
            {renderRight()}
          </div>
        )}
      </div>
    </Box>
  );
};

export default BillingRejected;
